// frontend/grautomates/src/components/ErrorBoundary.tsx
import { Component, ReactNode, ErrorInfo } from 'react';
import Button from './Button';
import { TEXT } from './Typography';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional fallback component to render on error */
  fallback?: ReactNode;
  /** Optional callback when error occurs */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** Component name for better error messages */
  componentName?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null, errorInfo: null };

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const { componentName, onError } = this.props;
    console.error(`[ErrorBoundary${componentName ? ` - ${componentName}` : ''}] Caught error:`, error);
    console.error('Component stack:', errorInfo.componentStack);
    this.setState({ errorInfo });

    // Call optional error callback
    if (onError) {
      onError(error, errorInfo);
    }
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleRefresh = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    const { hasError, error, errorInfo } = this.state;
    const { children, fallback, componentName } = this.props;

    if (hasError) {
      // Use custom fallback if provided
      if (fallback) {
        return fallback;
      }

      // Default error UI
      return (
        <div className="min-h-[200px] flex items-center justify-center p-6">
          <div className="max-w-md w-full bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            {/* Error Icon */}
            <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>

            <h3 className="text-lg font-semibold text-red-800 mb-2">
              Something went wrong
            </h3>

            <p className="text-sm text-red-600 mb-4">
              {componentName
                ? `An error occurred in the ${componentName} component.`
                : 'An unexpected error occurred while rendering this component.'}
            </p>

            {/* Error message (collapsed by default in production) */}
            {error?.message && (
              <details className="text-left mb-4 bg-white rounded-lg p-3 border border-red-100">
                <summary className="text-xs font-medium text-red-700 cursor-pointer">
                  Error details
                </summary>
                <p className={`mt-2 font-mono break-all ${TEXT.errorCaption}`}>
                  {error.message}
                </p>
                {errorInfo?.componentStack && (
                  <pre className={`mt-2 overflow-auto max-h-32 whitespace-pre-wrap ${TEXT.caption}`}>
                    {errorInfo.componentStack}
                  </pre>
                )}
              </details>
            )}

            {/* Action buttons */}
            <div className="flex gap-3 justify-center">
              <Button variant="dangerOutline" size="sm" onClick={this.handleRetry}>
                Try Again
              </Button>
              <Button variant="danger" onClick={this.handleRefresh}>
                Refresh Page
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return children;
  }
}

export default ErrorBoundary;
